import type { ComponentType } from "react";
import type {
  ModalComponentProps,
  ModalEntry,
  ModalEventClosePayload,
  ModalEventPushPayload,
  ModalId,
  ModalState,
} from "@/lib/modal/types";

type Listener = () => void;
type EventListener<T> = (payload: T) => void;

const CLOSE_ANIMATION_DURATION = 200;

let modalIdCounter = 0;

function generateModalId(): ModalId {
  modalIdCounter += 1;
  return `modal-${modalIdCounter}-${Date.now()}`;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function getComponentName(component: ComponentType<any>): string {
  return component.displayName || component.name || "UnknownModal";
}

function createModalStore() {
  let state: ModalState = { modals: [] };
  const listeners = new Set<Listener>();
  const pushListeners = new Set<EventListener<ModalEventPushPayload>>();
  const closeListeners = new Set<EventListener<ModalEventClosePayload>>();

  function getState(): ModalState {
    return state;
  }

  function setState(newState: ModalState): void {
    state = newState;
    listeners.forEach((listener) => listener());
  }

  function subscribe(listener: Listener): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
  }

  function emitPush(payload: ModalEventPushPayload): void {
    pushListeners.forEach((listener) => listener(payload));
  }

  function emitClose(payload: ModalEventClosePayload): void {
    closeListeners.forEach((listener) => listener(payload));
  }

  function onPushModal(
    listener: EventListener<ModalEventPushPayload>
  ): () => void {
    pushListeners.add(listener);
    return () => pushListeners.delete(listener);
  }

  function onCloseModal(
    listener: EventListener<ModalEventClosePayload>
  ): () => void {
    closeListeners.add(listener);
    return () => closeListeners.delete(listener);
  }

  function pushModal<TProps extends Record<string, unknown>>(
    component: ComponentType<TProps & ModalComponentProps>,
    props: TProps
  ): ModalId {
    const id = generateModalId();
    const name = getComponentName(component);
    const entry: ModalEntry = {
      id,
      name,
      component: component as ComponentType<ModalComponentProps>,
      props,
    };

    setState({
      modals: [...state.modals, entry],
    });

    emitPush({
      modalId: id,
      modalName: name,
      props,
      isReplace: false,
    });

    return id;
  }

  function popModal(modalId?: ModalId): void {
    if (state.modals.length === 0) return;

    let modalToClose: ModalEntry | undefined;

    if (modalId) {
      const modal = state.modals.find((m) => m.id === modalId);
      if (!modal || modal.isClosing) return;
      modalToClose = modal;
    } else {
      const nonClosing = state.modals.filter((m) => !m.isClosing);
      if (nonClosing.length === 0) return;
      modalToClose = nonClosing[nonClosing.length - 1];
    }

    if (!modalToClose) return;

    const closingId = modalToClose.id;

    // Mark as closing to trigger the exit animation
    setState({
      modals: state.modals.map((m) =>
        m.id === closingId ? { ...m, isClosing: true } : m
      ),
    });

    // Remove from array after the animation completes
    setTimeout(() => {
      setState({
        modals: getState().modals.filter((m) => m.id !== closingId),
      });
    }, CLOSE_ANIMATION_DURATION);

    emitClose({
      modalId: modalToClose.id,
      modalName: modalToClose.name,
      props: modalToClose.props,
      reason: "pop",
    });
  }

  function popAllModals(): void {
    const modalsToClose = state.modals.filter((m) => !m.isClosing);
    if (modalsToClose.length === 0) return;

    const closingIds = new Set(modalsToClose.map((m) => m.id));

    // Mark all as closing to trigger exit animations
    setState({
      modals: state.modals.map((m) => ({ ...m, isClosing: true })),
    });

    // Remove after animation completes
    setTimeout(() => {
      setState({
        modals: getState().modals.filter((m) => !closingIds.has(m.id)),
      });
    }, CLOSE_ANIMATION_DURATION);

    modalsToClose.forEach((modal) => {
      emitClose({
        modalId: modal.id,
        modalName: modal.name,
        props: modal.props,
        reason: "popAll",
      });
    });
  }

  function replaceWithModal<TProps extends Record<string, unknown>>(
    component: ComponentType<TProps & ModalComponentProps>,
    props: TProps
  ): ModalId {
    const id = generateModalId();
    const name = getComponentName(component);
    const entry: ModalEntry = {
      id,
      name,
      component: component as ComponentType<ModalComponentProps>,
      props,
    };

    const currentModal = [...state.modals]
      .reverse()
      .find((m: ModalEntry) => !m.isClosing);

    // Skip overlay animation on replacement modals to prevent flash
    entry.skipOverlayAnimation = !!currentModal;

    setState({
      modals: [
        ...state.modals.map((m) =>
          m.id === currentModal?.id
            ? { ...m, isClosing: true, skipOverlayAnimation: true }
            : m
        ),
        entry,
      ],
    });

    if (currentModal) {
      // Remove the old modal after its exit animation completes
      setTimeout(() => {
        setState({
          modals: getState().modals.filter((m) => m.id !== currentModal.id),
        });
      }, CLOSE_ANIMATION_DURATION);

      emitClose({
        modalId: currentModal.id,
        modalName: currentModal.name,
        props: currentModal.props,
        reason: "replace",
      });
    }

    emitPush({
      modalId: id,
      modalName: name,
      props,
      isReplace: true,
      replacedModal: currentModal
        ? {
            modalId: currentModal.id,
            modalName: currentModal.name,
            props: currentModal.props,
          }
        : undefined,
    });

    return id;
  }

  return {
    getState,
    subscribe,
    pushModal,
    popModal,
    popAllModals,
    replaceWithModal,
    onPushModal,
    onCloseModal,
  };
}

export const modalStore = createModalStore();

export type ModalStore = ReturnType<typeof createModalStore>;
